import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  DropdownButton, Dropdown
} from 'react-bootstrap';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const LeadershipMessagesManager = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Options
  const [availableDesignations, setAvailableDesignations] = useState([]);
  
  const [formData, setFormData] = useState({
    leader_name: '',
    designation: '',
    display_order: 1,
    profile_photo: '',
    message_title: '',
    message_content: '',
    closing_quote: '',
    is_active: true,
    show_on_homepage: false,
    created_date: new Date().toISOString().split('T')[0]
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchMessages();
    fetchOptions();
  }, []);

  const resetForm = () => {
    setFormData({
      leader_name: '',
      designation: '',
      display_order: 1,
      profile_photo: '',
      message_title: '',
      message_content: '',
      closing_quote: '',
      is_active: true,
      show_on_homepage: false,
      created_date: new Date().toISOString().split('T')[0]
    });
    setPhotoPreview(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/leadership_messages.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedMessages = response.data.data.map(message => ({
          ...message,
          id: parseInt(message.id),
          display_order: parseInt(message.display_order),
          is_active: message.is_active === "1" || message.is_active === 1,
          show_on_homepage: message.show_on_homepage === "1" || message.show_on_homepage === 1
        })).sort((a, b) => a.display_order - b.display_order);
        
        setMessages(processedMessages);
      } else {
        setError('Failed to load leadership messages');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/leadership_messages.php?options=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setAvailableDesignations(response.data.data.designations || []);
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Set file for upload
      setFormData(prev => ({
        ...prev,
        photo_file: file
      }));
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({
      ...prev,
      profile_photo: '',
      photo_file: null
    }));
  };

  const uploadPhoto = async (file) => {
    try {
      setUploadingPhoto(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('profile_photo', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_leadership_photo.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.profile_photo;
      }
      return null;
    } catch (err) {
      console.error('Photo upload error:', err);
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDesignationSelect = (designation) => {
    setFormData(prev => ({
      ...prev,
      designation: designation
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove photo_file from dataToSend if it exists
      if (dataToSend.photo_file) {
        delete dataToSend.photo_file;
      }
      
      // If there's a new photo to upload
      if (formData.photo_file) {
        const uploadedUrl = await uploadPhoto(formData.photo_file);
        if (uploadedUrl) {
          dataToSend.profile_photo = uploadedUrl;
        } else {
          setError('Failed to upload profile photo');
          return;
        }
      }
      
      // If order is not specified, set to next available
      if (!dataToSend.display_order || dataToSend.display_order <= 0) {
        const maxOrder = messages.length > 0 
          ? Math.max(...messages.map(message => parseInt(message.display_order))) 
          : 0;
        dataToSend.display_order = maxOrder + 1;
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingMessage) {
        response = await axios.put(
          `${API_BASE_URL}/leadership_messages.php?id=${editingMessage.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/leadership_messages.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingMessage ? 'Leadership message updated!' : 'Leadership message added!');
        setShowModal(false);
        resetForm();
        setEditingMessage(null);
        fetchMessages();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving leadership message: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this leadership message? This will also delete the associated profile photo.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/leadership_messages.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Leadership message deleted!');
          fetchMessages();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete leadership message: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting leadership message');
        console.error(err);
      }
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      leader_name: message.leader_name,
      designation: message.designation,
      display_order: parseInt(message.display_order),
      profile_photo: message.profile_photo || '',
      message_title: message.message_title || '',
      message_content: message.message_content || '',
      closing_quote: message.closing_quote || '',
      is_active: message.is_active === true || message.is_active === "1" || message.is_active === 1,
      show_on_homepage: message.show_on_homepage === true || message.show_on_homepage === "1" || message.show_on_homepage === 1,
      created_date: message.created_date || new Date().toISOString().split('T')[0]
    });
    
    // Set photo preview if profile_photo exists
    if (message.profile_photo) {
      setPhotoPreview(message.profile_photo);
    }
    
    setShowModal(true);
  };

  const handleToggleStatus = async (message, field) => {
    try {
      const currentStatus = message[field] === true || message[field] === "1" || message[field] === 1;
      const newStatus = !currentStatus;
      
      const updatedData = {
        leader_name: message.leader_name,
        designation: message.designation,
        display_order: parseInt(message.display_order),
        profile_photo: message.profile_photo || '',
        message_title: message.message_title || '',
        message_content: message.message_content,
        closing_quote: message.closing_quote || '',
        is_active: field === 'is_active' ? (newStatus ? 1 : 0) : message.is_active,
        show_on_homepage: field === 'show_on_homepage' ? (newStatus ? 1 : 0) : message.show_on_homepage,
        created_date: message.created_date || new Date().toISOString().split('T')[0]
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/leadership_messages.php?id=${message.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchMessages();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMessage(null);
    resetForm();
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(messages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index + 1
    }));
    
    setMessages(updatedItems);
    
    // Send update to server
    try {
      const token = localStorage.getItem('token');
      const reorderData = updatedItems.map(item => ({
        id: item.id,
        display_order: item.display_order
      }));
      
      const response = await axios.post(`${API_BASE_URL}/leadership_messages.php`, {
        reorder: reorderData
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data.success) {
        setError('Failed to save order changes');
        fetchMessages(); // Revert to server state
      }
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to save order changes');
      fetchMessages(); // Revert to server state
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${UPLOAD_BASE_URL}${photoPath}`;
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading leadership messages...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Leadership Messages Manager</h2>
            <p className="text-muted">Manage leadership messages with drag & drop ordering</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              const nextOrder = messages.length > 0 
                ? Math.max(...messages.map(message => parseInt(message.display_order))) + 1 
                : 1;
              setFormData(prev => ({ 
                ...prev, 
                display_order: nextOrder,
                is_active: true,
                show_on_homepage: false,
                created_date: new Date().toISOString().split('T')[0]
              }));
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Leadership Message
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="leadership-messages">
                {(provided) => (
                  <Table responsive hover ref={provided.innerRef} {...provided.droppableProps}>
                    <thead>
                      <tr>
                        <th width="60">Drag</th>
                        <th width="80">Photo</th>
                        <th>Leader Name</th>
                        <th>Designation</th>
                        <th>Message Title</th>
                        <th>Order</th>
                        <th width="120">Status</th>
                        <th width="120">Homepage</th>
                        <th>Created</th>
                        <th width="150">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((message, index) => (
                        <Draggable key={message.id} draggableId={message.id.toString()} index={index}>
                          {(provided) => (
                            <tr 
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <td {...provided.dragHandleProps}>
                                <i className="fas fa-grip-vertical text-muted"></i>
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  {message.profile_photo ? (
                                    <Image 
                                      src={getPhotoUrl(message.profile_photo)} 
                                      alt={message.leader_name}
                                      roundedCircle
                                      style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                      className="border"
                                    />
                                  ) : (
                                    <div className="text-muted fs-4">
                                      <i className="fas fa-user-circle"></i>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                <strong>{message.leader_name}</strong>
                              </td>
                              <td>
                                <Badge bg="info" className="text-wrap" style={{maxWidth: '150px'}}>
                                  {message.designation}
                                </Badge>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {message.message_title ? truncateText(message.message_title, 50) : 'No title'}
                                </small>
                              </td>
                              <td>
                                <Badge bg="secondary">{message.display_order}</Badge>
                              </td>
                              <td>
                                <Form.Check
                                  type="switch"
                                  checked={message.is_active}
                                  onChange={() => handleToggleStatus(message, 'is_active')}
                                  label={message.is_active ? 'Active' : 'Inactive'}
                                  key={`active-${message.id}-${message.is_active}`}
                                />
                              </td>
                              <td>
                                <Form.Check
                                  type="switch"
                                  checked={message.show_on_homepage}
                                  onChange={() => handleToggleStatus(message, 'show_on_homepage')}
                                  label={message.show_on_homepage ? 'Show' : 'Hide'}
                                  key={`homepage-${message.id}-${message.show_on_homepage}`}
                                />
                              </td>
                              <td>
                                <small className="text-muted">
                                  {formatDate(message.created_date)}
                                </small>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEdit(message)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(message.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </Table>
                )}
              </Droppable>
            </DragDropContext>
            
            {messages.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-users fa-3x mb-3"></i>
                <p>No leadership messages found. Add your first message!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Leadership Message Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingMessage ? 'Edit Leadership Message' : 'Add Leadership Message'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Leader Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="leader_name"
                      value={formData.leader_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Dr. John Smith"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Designation *</Form.Label>
                    <DropdownButton
                      variant="outline-secondary"
                      title={formData.designation || "Select Designation"}
                      onSelect={handleDesignationSelect}
                      className="w-100"
                    >
                      {availableDesignations.map((designation) => (
                        <Dropdown.Item key={designation} eventKey={designation}>
                          {designation}
                        </Dropdown.Item>
                      ))}
                    </DropdownButton>
                    {formData.designation && (
                      <div className="mt-2">
                        Selected: <Badge bg="info">{formData.designation}</Badge>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Display Order</Form.Label>
                    <Form.Control
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <Form.Text className="text-muted">
                      Lower numbers appear first
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Created Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="created_date"
                      value={formData.created_date}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Inactive messages won't be shown
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Homepage</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="show_on_homepage"
                        label="Show on Homepage"
                        checked={formData.show_on_homepage}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Display on homepage
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Message Title (Optional)</Form.Label>
                <Form.Control
                  type="text"
                  name="message_title"
                  value={formData.message_title}
                  onChange={handleInputChange}
                  placeholder="e.g., Welcome Message, Vision for the Future"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Message Content *</Form.Label>
                <Form.Control
                  as="textarea"
                  name="message_content"
                  value={formData.message_content}
                  onChange={handleTextareaChange}
                  rows={6}
                  placeholder="Write the leadership message here..."
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Closing Quote (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  name="closing_quote"
                  value={formData.closing_quote}
                  onChange={handleTextareaChange}
                  rows={2}
                  placeholder="e.g., Best regards, Thank you, etc."
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Profile Photo</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      disabled={uploadingPhoto}
                    />
                    <Form.Text className="text-muted">
                      Upload a profile photo (recommended: 400x400px, max 2MB)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Photo Preview */}
              {(photoPreview || formData.profile_photo) && (
                <div className="mb-3">
                  <Form.Label>Photo Preview</Form.Label>
                  <div className="d-flex align-items-center">
                    <Image 
                      src={photoPreview?.startsWith('data:') ? photoPreview : getPhotoUrl(formData.profile_photo)}
                      alt="Preview" 
                      roundedCircle
                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                      className="me-3 border"
                    />
                    
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={removePhoto}
                    >
                      <i className="fas fa-trash me-1"></i> Remove Photo
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Preview Section */}
              <Card className="border-secondary mt-3">
                <Card.Header>
                  <h6 className="mb-0">Preview</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3} className="text-center">
                      {(photoPreview || formData.profile_photo) ? (
                        <Image 
                          src={photoPreview?.startsWith('data:') ? photoPreview : getPhotoUrl(formData.profile_photo)}
                          alt={formData.leader_name || 'Leader'} 
                          roundedCircle
                          style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                          className="border mb-3"
                        />
                      ) : (
                        <div className="text-muted fs-1 mb-3">
                          <i className="fas fa-user-circle"></i>
                        </div>
                      )}
                      <h5 className="mb-1">{formData.leader_name || 'Leader Name'}</h5>
                      <div className="text-muted">
                        <Badge bg="info">{formData.designation || 'Designation'}</Badge>
                      </div>
                    </Col>
                    <Col md={9}>
                      {formData.message_title && (
                        <h4 className="mb-3">{formData.message_title}</h4>
                      )}
                      <div className="mb-3">
                        {formData.message_content || 'Message content will appear here...'}
                      </div>
                      {formData.closing_quote && (
                        <div className="text-muted fst-italic">
                          "{formData.closing_quote}"
                        </div>
                      )}
                      <div className="mt-3 small text-muted">
                        <div className="d-flex">
                          <div className="me-4">
                            <i className="fas fa-sort-numeric-up me-1"></i>
                            Order: <span className="ms-2">{formData.display_order}</span>
                          </div>
                          <div className="me-4">
                            <i className="fas fa-circle me-1"></i>
                            Status: 
                            <span className={`ms-2 ${formData.is_active ? 'text-success' : 'text-warning'}`}>
                              {formData.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="me-4">
                            <i className="fas fa-home me-1"></i>
                            Homepage: 
                            <span className={`ms-2 ${formData.show_on_homepage ? 'text-success' : 'text-muted'}`}>
                              {formData.show_on_homepage ? 'Visible' : 'Hidden'}
                            </span>
                          </div>
                          <div>
                            <i className="fas fa-calendar me-1"></i>
                            Date: <span className="ms-2">{formatDate(formData.created_date)}</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              {editingMessage && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <div className="row">
                      <div className="col-md-6">
                        <i className="fas fa-id-badge me-2"></i>
                        ID: {editingMessage.id}
                      </div>
                      <div className="col-md-6">
                        <i className="fas fa-sync-alt me-2"></i>
                        Last Updated: {formatDate(editingMessage.updated_at)}
                      </div>
                    </div>
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingPhoto}>
                {uploadingPhoto && <Spinner animation="border" size="sm" className="me-2" />}
                {editingMessage ? 'Update' : 'Add'} Leadership Message
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default LeadershipMessagesManager;